<script setup>
/* eslint-disable vue/no-v-html */
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import ConfigurationRadioSelection from '../../ConfigurationRadioSelection';
import ConfigurationRadioSelectionV3 from '../../ConfigurationRadioSelectionV3';
import { useMainStore } from '../../../store/index.js';

import '../../../styles/configurator-section.css';
import '../../../styles/configurator-selection.css';

const windowWidth = ref(window.innerWidth);

const store = useMainStore();

const emit = defineEmits(['select']);

const props = defineProps({
  items: {
    type: Object,
    default: null,
  },
  images: {
    type: Object,
    required: true,
  },
  isSpecEB: {
    type: Boolean,
    required: true,
  },
  chassisDescriptionKey: {
    type: String,
    required: false,
    default: '',
  },
  showDimensionsSubheader: {
    type: Boolean,
    default: true,
  },
});

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const body = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'body');
});

const cabHeight = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'cabHeight');
});

const cabHeightDisabledSelections = computed(() => {
  const disabledSelections = { options: [], texts: {} };
  if (!!body.value) {
    if (store.selected.bodyBuild?.body?.id !== 'without' && cabHeight.value) {
      disabledSelections.options = body.value.options.find(o => o.id === 'without')?.dependencies?.flatMap(d => d);
      disabledSelections.texts = {};
      disabledSelections.options.forEach(o => disabledSelections.texts[o] = `${o}DisabledText`);
    }
  }
  return disabledSelections;
});

watch(cabHeightDisabledSelections, () => {
  if (!!body.value) {
    if (store.selected.bodyBuild?.body?.id !== 'without') {
      const bodyDependencies = store.selected.bodyBuild.body.dependencies?.flatMap(d => d);
      if (bodyDependencies.length) {
        const selectedCabHeight = cabHeight.value.options.find(cH => cH.id === bodyDependencies[0]);
        store.selected.bodyBuild.cabHeight = selectedCabHeight;
        emit('select', { option: selectedCabHeight, type: cabHeight.value.id });
      }
    }
  }
});
</script>

<template>
  <section
    v-if="props.items"
    id="bodyBuild"
    class="configurator-section"
  >
    <h2>{{ $i18n('bodyBuildHeaderTitle') }}</h2>
    <h3
      v-if="props.showDimensionsSubheader && (props.images.side && props.images.isSide)"
    >
      {{ $i18n('dimensionsTitle') }}
    </h3>
    <div class="configurator-section__row configurator-section__image-container sdds-u-p3">
      <div
        v-if="props.images.side"
        class="configurator-section__image side-image-custom-margin"
      >
        <img
          :src="props.images.side"
          alt="Truck - side view"
        >
      </div>
      <div
        v-if="props.chassisDescriptionKey"
        class="configurator-section__image-text"
        v-html="$i18n(props.chassisDescriptionKey)"
      />
    </div>
    <ConfigurationRadioSelection
      v-if="body"
      v-model="store.selected.bodyBuild[body.id]"
      :type="body.id"
      :options="body.options"
      @select="emit('select', $event)"
    />
    <ConfigurationRadioSelectionV3
      v-if="cabHeight"
      v-model="store.selected.bodyBuild[cabHeight.id]"
      :type="cabHeight.id"
      :options="cabHeight.options"
      :title="cabHeight.title"
      :disabled-selections="cabHeightDisabledSelections.options"
      :disabled-texts="cabHeightDisabledSelections.texts"
      @select="emit('select', $event)"
    />
  </section>
</template>

<style scoped>
.sdds-divider-dark-vertical {
  height: var(--sdds-spacing-layout-72);
  margin-top: var(--sdds-spacing-element-48);
  align-self: center;
}

.configurator-section__image {
  max-width: 70%;
}

.configurator-section__image>img {
  max-width: 100%;
  object-fit: cover;
}

.is-side-image-custom-margin,
.side-image-custom-margin {
  margin-top: -12rem;
}

@media only screen and (max-width: 1294px) {
  .configurator-section__image>img {
    height: initial;
  }

  .is-side-image-custom-margin,
  .side-image-custom-margin {
    margin-top: -10rem;
  }
}

@media only screen and (max-width: 1000px) {
  .configurator-section__image {
    width: 90%;
  }

  .sdds-divider-dark-vertical {
    display: none;
  }

  .configurator-section__row {
    flex-direction: column;
    align-items: center;
  }
}
</style>
